import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PriceDiscoveryService } from '../../services/price-discovery.service';

@Component({
  selector: 'app-inventory-pr-modal',
  templateUrl: './inventory-pr-modal.component.html',
  styleUrls: ['./inventory-pr-modal.component.scss']
})
export class InventoryPrModalComponent implements OnChanges, OnDestroy {
  @Input() isVisible = false;
  @Input() entityId = '';
  @Input() searchTags: any[] = [];

  @Output() closeModal = new EventEmitter<void>();
  @Output() searchItems = new EventEmitter<any[]>();

  isInventoryPrLoading = false;
  inventoryPrItems: any[] = [];
  inventoryPrPage = 1;
  inventoryPrHasMore = false;

  private destroy$ = new Subject<void>();

  constructor(private priceDiscoveryService: PriceDiscoveryService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible']) {
      if (this.isVisible) {
        this.inventoryPrPage = 1;
        this.inventoryPrItems = [];
        this.loadInventoryPrItems();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleModal(): void {
    this.closeModal.emit();
  }

  loadInventoryPrItems(): void {
    if (!this.entityId) return;
    this.isInventoryPrLoading = true;
    this.priceDiscoveryService.getPendingInventoryPurchaseRequests(this.entityId, this.inventoryPrPage, 5)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          const actualData = response?.data || response || {};
          let requests = actualData.requests || [];
          requests = requests.map((req: any) => ({
            ...req,
            expanded: false,
            itemDetails: (req.itemDetails || req.item_details || []).map((item: any) => ({
              ...item,
              closingStock: item.closingStock !== undefined ? item.closingStock : item.closing_stock,
              closingStockDate: item.closingStockDate || item.closing_stock_date,
              selected: false
            }))
          }));
          this.inventoryPrItems = [...this.inventoryPrItems, ...requests];

          const pagination = actualData.pagination || {};
          this.inventoryPrHasMore = (pagination.currentPage || pagination.current_page) < (pagination.totalPages || pagination.total_pages);
          this.isInventoryPrLoading = false;
        },
        (error: any) => {
          console.error('Failed to load Inventory PR items', error);
          this.isInventoryPrLoading = false;
        }
      );
  }

  loadMoreInventoryPrItems(): void {
    if (!this.inventoryPrHasMore || this.isInventoryPrLoading) return;
    this.inventoryPrPage++;
    this.loadInventoryPrItems();
  }

  onInventoryPrScroll(event: any): void {
    const target = event.target;
    if (target.scrollHeight - target.scrollTop <= target.clientHeight + 50) {
      this.loadMoreInventoryPrItems();
    }
  }

  toggleInventoryPrRow(item: any): void {
    item.expanded = !item.expanded;
  }

  isPrItemProcessed(item: any): boolean {
    const status = item.processStatus || item.process_status || 'Pending';
    return status.toLowerCase() === 'processed' || status.toLowerCase() === 'completed';
  }

  isPrItemSelectable(item: any): boolean {
    return !this.isPrItemProcessed(item);
  }

  getSelectedPrItems(req: any): any[] {
    const items = req.itemDetails || req.item_details || [];
    return items.filter((item: any) => item.selected && this.isPrItemSelectable(item));
  }

  getUnprocessedPrItems(req: any): any[] {
    const items = req.itemDetails || req.item_details || [];
    return items.filter((item: any) => this.isPrItemSelectable(item));
  }

  getSelectedPrItemsCount(req: any): number {
    return this.getSelectedPrItems(req).length;
  }

  getUnprocessedPrItemsCount(req: any): number {
    return this.getUnprocessedPrItems(req).length;
  }

  isPrSelectAllChecked(req: any): boolean {
    const unprocessed = this.getUnprocessedPrItems(req);
    if (unprocessed.length === 0) return false;
    return unprocessed.every((item: any) => item.selected);
  }

  toggleSelectAllPrItems(req: any, event: any): void {
    const checked = event.target ? event.target.checked : event;
    const unprocessed = this.getUnprocessedPrItems(req);
    unprocessed.forEach((item: any) => item.selected = checked);
  }

  toggleItemSelection(item: any): void {
    if (this.isPrItemSelectable(item)) {
      item.selected = !item.selected;
    }
  }

  searchSelectedInventoryPrItems(req: any): void {
    const selectedItems = this.getSelectedPrItems(req);
    if (selectedItems.length === 0) return;

    const reqId = req._id || req.id;
    const itemIds = selectedItems.map((item: any) => item._id || item.id || item.itemName || item.name).filter(Boolean);

    if (reqId && itemIds.length > 0) {
      this.priceDiscoveryService.updateInventoryPurchaseRequestStatus(
        reqId,
        undefined,
        this.entityId,
        itemIds,
        'Processed'
      ).subscribe((res: any) => {
        console.log(`Successfully updated selected items status to Processed for request ${reqId}`);
        selectedItems.forEach((item: any) => item.processStatus = 'Processed');
        const allItems = req.itemDetails || req.item_details || [];
        if (allItems.every((i: any) => this.isPrItemProcessed(i))) {
          req.processStatus = 'Processed';
        } else {
          req.processStatus = 'Inprogress';
        }
        this.searchItems.emit(selectedItems);
        this.closeModal.emit();
      }, (error: any) => {
        console.error('Failed to update inventory PR selected items status', error);
        this.searchItems.emit(selectedItems);
        this.closeModal.emit();
      });
    } else {
      this.searchItems.emit(selectedItems);
      this.closeModal.emit();
    }
  }

  searchAllInventoryPrItems(req: any): void {
    const unprocessedItems = this.getUnprocessedPrItems(req);
    if (unprocessedItems.length === 0) return;

    const reqId = req._id || req.id;
    const itemIds = unprocessedItems.map((item: any) => item._id || item.id || item.itemName || item.name).filter(Boolean);

    if (reqId) {
      this.priceDiscoveryService.updateInventoryPurchaseRequestStatus(
        reqId,
        'Processed',
        this.entityId,
        itemIds,
        'Processed'
      ).subscribe(() => {
        console.log(`Successfully updated status to Processed for request ${reqId}`);
        unprocessedItems.forEach((item: any) => item.processStatus = 'Processed');
        req.processStatus = 'Processed';
        this.searchItems.emit(unprocessedItems);
        this.closeModal.emit();
      }, (error: any) => {
        console.error('Failed to update inventory PR status', error);
        this.searchItems.emit(unprocessedItems);
        this.closeModal.emit();
      });
    } else {
      this.searchItems.emit(unprocessedItems);
      this.closeModal.emit();
    }
  }

  isInventoryPrRequestAdded(req: any): boolean {
    const items = req.itemDetails || req.item_details || [];
    if (items.length === 0) return false;
    return items.every((item: any) => this.isInventoryPrAdded(item) || this.isPrItemProcessed(item));
  }

  isInventoryPrAdded(item: any): boolean {
    const name = item.name || item.itemName || item.item_name || '';
    const qty = item.qty || item.quantity || '';
    const unit = item.unit || item.uom || '';
    const searchString = `${name} ${qty}${unit}`.trim().toLowerCase();

    return this.searchTags.some(t => t.name.toLowerCase() === searchString);
  }
}
